/**
 * SDK diagnostics (FLO-960) — the SDK's own lifecycle and failure stream.
 *
 * The SDK had no way to say "this start failed, and here is how far it got".
 * Prod forensics meant reading the bot's log, where everything before
 * `client-ready` is invisible, and the browser side was a console line the
 * visitor's page never keeps. This module ships a narrow, versioned allowlist
 * of client-observed lifecycle facts to first-party intake:
 *
 *   POST {apiUrl}/sites/telemetry
 *   { clientKey, consent: 'not-required' | 'granted', events: [ ... ] }
 *
 * The wire contract, stated once here so it cannot drift from the code:
 *
 *   item = {
 *     schemaVersion: 1,
 *     eventId:       uuid v4, unique per event (server dedupes on it),
 *     sequence:      integer 0..1e6, monotonic per page load,
 *     correlationId: 'session_<uuid v4>' — the SDK's per-start writer id,
 *     event:         one of SdkDiagnosticEventName,
 *     agentMode:     'demo' | 'website' | 'onboarding' | 'support',
 *     sdkVersion:    bounded semver,
 *     timestamp:     finite epoch ms,
 *     attributes?:   { reason?, stage?, durationMs?, sessionDurationMs?,
 *                      prefetched? } — allowlisted keys only
 *   }
 *
 * Batches: ≤50 events, ≤64 KiB body, debounce 3s, keepalive on the final flush.
 *
 * Three properties are load-bearing and every one of them is a test:
 *
 * 1. **A closed vocabulary.** Event names, attribute keys, reason codes, and
 *    stages are enumerated here. There is no free-text field, so no error
 *    message, URL, DOM node, transcript, tool payload, or token can ride an
 *    event even by accident. `sanitizeDiagnosticAttributes` is the single
 *    filter, and unknown keys are DROPPED rather than forwarded.
 * 2. **No new identity.** Correlation reuses the per-start writer id the SDK
 *    already mints (`session-id.ts`), which the API already validates and
 *    forwards. Nothing is written to localStorage; there is no anonymous id,
 *    visitor token, or cross-page identifier. Losing the page loses the thread.
 * 3. **Reporting never touches the path it reports on.** Delivery rides the
 *    existing bounded {@link EventQueue} (debounce, ≤50 per batch, 60 KiB body
 *    ceiling, shared 64 KiB keepalive budget). Every entry point is wrapped, so
 *    a thrown emit costs the visitor nothing.
 *
 * Privacy posture: this is DIAGNOSTICS, and it answers to the same consent and
 * collection switches as analytics. It does not instrument the host page — no
 * fetch wrapper, no console capture, no global error handler — and it does not
 * borrow the customer's Sentry/PostHog. Turning collection off, or withdrawing
 * consent, clears the queue UNSENT and unbinds delivery.
 *
 * The pure helpers (id minting, attribute filtering, stage ordering, duration
 * bounds) are exported so the vitest `node` env can exercise them for real;
 * the wiring in onboarding-sdk.ts is pinned by source assertions, the same
 * approach consent-mode.ts and session-id.ts use.
 */

import { EventQueue, sendEventBatch } from './event-queue';
import { logger as defaultLogger, type Logger } from './logger';
import { reportedSdkVersion } from './sdk-version';

/** Body shape version. Bump only with a server-side counterpart. */
export const DIAGNOSTIC_SCHEMA_VERSION = 1;

/** Endpoint path, appended to the configured apiUrl. */
export const SDK_DIAGNOSTICS_PATH = '/sites/telemetry';

/**
 * Ceiling on a startup-window duration (click → bot ready / first audio).
 * Matches `VOICE_STARTUP_WINDOW_MS`, so a duration the startup timeline would
 * have abandoned can never arrive here as a "real" 12-minute startup.
 */
export const MAX_STARTUP_DURATION_MS = 120_000;

/** Ceiling on a whole session's duration (24h — a page left open overnight). */
export const MAX_SESSION_DURATION_MS = 86_400_000;

/**
 * The window in which a healthy start reaches bot-ready. Expiry emits ONE
 * `bot_ready_timeout` and is not retried: a session that never becomes ready
 * is a fact, not a stream.
 */
export const BOT_READY_TIMEOUT_MS = 120_000;

/** Server-side sequence bound; the counter stops here rather than wrapping. */
export const MAX_SEQUENCE = 1_000_000;

/** The closed event vocabulary (design §SDK diagnostics). */
export type SdkDiagnosticEventName =
  | 'connect_started'
  | 'connect_failed'
  | 'mic_denied'
  | 'transport_disconnected'
  | 'bot_ready_timeout'
  | 'first_audio'
  | 'session_ended'
  | 'startup_stage';

/**
 * Which product surface this report came from.
 *
 * `support` is not `onboarding`: both are the host-page agent, but support mode
 * is the dormant, user-initiated posture (`activation: 'on_demand'`, FLO-534)
 * and it fails for different reasons. Collapsing them would make a support
 * outage indistinguishable from a proactive onboarding one.
 */
export type SdkDiagnosticAgentMode = 'demo' | 'website' | 'onboarding' | 'support';

/** How far a start got. Ordered — {@link stageAtLeast} compares by rank. */
export type SdkDiagnosticStage =
  | 'idle'
  | 'session_open'
  | 'transport_connect'
  | 'bot_ready'
  | 'first_audio';

/**
 * Fine-grained startup milestones, straight off the existing measurement in
 * `voice-startup-timing.ts` (`VoiceStartupMark`, snake_cased). These are what
 * turn "the start failed" into "the start stalled between the spawn response
 * and the Daily join", which is the segment the bot's own log cannot see.
 *
 * Only the website-agent path arms the startup timeline today, so only that
 * mode produces `startup_stage` events — the coarse {@link SdkDiagnosticStage}
 * is what the other modes report.
 */
export type SdkStartupStage =
  | 'conversation_opened'
  | 'start_session_response'
  | 'daily_bundle_loaded'
  | 'daily_joined'
  | 'bot_participant_seen'
  | 'client_ready_sent'
  | 'bot_ready'
  | 'first_bot_speech';

/** Every `stage` value intake accepts, coarse and fine alike. */
export type SdkDiagnosticStageValue = SdkDiagnosticStage | SdkStartupStage;

/**
 * Closed reason vocabulary. Every value is a category the SDK can actually
 * distinguish at the call site; nothing is derived from an exception message.
 */
export type SdkDiagnosticReason =
  | 'permission_denied'
  | 'no_device'
  | 'device_missing'
  | 'device_busy'
  | 'start_session_failed'
  | 'transport_error'
  | 'bot_ready_timeout'
  | 'transport_closed'
  | 'client_requested'
  | 'superseded'
  | 'page_hidden'
  | 'unknown';

/** Consent posture stamped on the batch. `null` = suppressed, never sent. */
export type SdkDiagnosticConsent = 'not-required' | 'granted';

/**
 * The microphone categories a call site can genuinely distinguish. There is
 * deliberately no catch-all: an unclassified `getUserMedia` failure reports
 * NOTHING rather than being filed as a denial or a missing device.
 */
export type SdkDiagnosticMicReason =
  | 'permission_denied'
  | 'device_missing'
  | 'device_busy'
  | 'no_device';

/** The only attribute keys that may appear on the wire. */
export const ALLOWED_ATTRIBUTE_KEYS = [
  'reason',
  'stage',
  'durationMs',
  'sessionDurationMs',
  'prefetched',
] as const;

export const ALLOWED_REASONS: readonly SdkDiagnosticReason[] = [
  'permission_denied',
  'no_device',
  'device_missing',
  'device_busy',
  'start_session_failed',
  'transport_error',
  'bot_ready_timeout',
  'transport_closed',
  'client_requested',
  'superseded',
  'page_hidden',
  'unknown',
];

export const ALLOWED_STAGES: readonly SdkDiagnosticStage[] = [
  'idle',
  'session_open',
  'transport_connect',
  'bot_ready',
  'first_audio',
];

/** Fine milestones accepted as a `stage` value (startup_stage events). */
export const ALLOWED_STARTUP_STAGES: readonly SdkStartupStage[] = [
  'conversation_opened',
  'start_session_response',
  'daily_bundle_loaded',
  'daily_joined',
  'bot_participant_seen',
  'client_ready_sent',
  'bot_ready',
  'first_bot_speech',
];

/**
 * `VoiceStartupMark` -> wire stage name. Lives here (not in the SDK) so the
 * mapping is one table rather than a switch at the registration site, and so a
 * new mark added to the timeline cannot silently become a `stage` value intake
 * does not accept — it just reports nothing until the table is extended.
 */
const STARTUP_MARK_TO_STAGE: Record<string, SdkStartupStage> = {
  conversationOpened: 'conversation_opened',
  startSessionResponse: 'start_session_response',
  dailyBundleLoaded: 'daily_bundle_loaded',
  dailyJoined: 'daily_joined',
  botParticipantSeen: 'bot_participant_seen',
  clientReadySent: 'client_ready_sent',
  botReady: 'bot_ready',
  firstBotSpeech: 'first_bot_speech',
};

/** Wire name for a startup mark, or undefined for one we do not report. */
export function startupStageForMark(mark: string): SdkStartupStage | undefined {
  return Object.prototype.hasOwnProperty.call(STARTUP_MARK_TO_STAGE, mark)
    ? STARTUP_MARK_TO_STAGE[mark]
    : undefined;
}

const STAGE_RANK: Record<SdkDiagnosticStage, number> = {
  idle: 0,
  session_open: 1,
  transport_connect: 2,
  bot_ready: 3,
  first_audio: 4,
};

/** True when `stage` has reached or passed `floor`. */
export function stageAtLeast(stage: SdkDiagnosticStage, floor: SdkDiagnosticStage): boolean {
  return STAGE_RANK[stage] >= STAGE_RANK[floor];
}

/** Is this a stage value intake accepts (coarse milestone or fine mark)? */
export function isAllowedStage(value: unknown): value is SdkDiagnosticStageValue {
  return (
    typeof value === 'string' &&
    ((ALLOWED_STAGES as readonly string[]).includes(value) ||
      (ALLOWED_STARTUP_STAGES as readonly string[]).includes(value))
  );
}

/** Primitive attributes an event may carry. Absent keys are omitted. */
export interface SdkDiagnosticAttributes {
  reason?: SdkDiagnosticReason;
  stage?: SdkDiagnosticStageValue;
  durationMs?: number;
  sessionDurationMs?: number;
  prefetched?: boolean;
}

/** One wire item. Field order matches the contract doc. */
export interface SdkDiagnosticEventItem {
  schemaVersion: number;
  eventId: string;
  sequence: number;
  correlationId: string;
  event: SdkDiagnosticEventName;
  agentMode: SdkDiagnosticAgentMode;
  sdkVersion: string;
  timestamp: number;
  attributes?: SdkDiagnosticAttributes;
}

/**
 * The writer-correlation id shape: `session_` + a v4 UUID, matching the strict
 * pattern the API already applies to `sessionData.sessionId`
 * (sites.controller.ts) and to this endpoint's intake.
 *
 * Version AND variant are pinned. A looser "any UUID" check would let a
 * non-v4 value through here and have intake drop the whole event — a silent
 * loss of exactly the reports this module exists to deliver, which is worse
 * than replacing the id up front.
 */
const CORRELATION_ID_PATTERN =
  /^session_[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isCorrelationId(value: unknown): value is string {
  return typeof value === 'string' && CORRELATION_ID_PATTERN.test(value);
}

/**
 * A random id for one event. `crypto.randomUUID` is present in every browser
 * that can also run this transport; the fallback keeps a weird UA from
 * dropping the whole report on an id it cannot mint.
 */
export function newDiagnosticEventId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through to the hex path */
  }
  let out = '';
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      for (const byte of bytes) out += byte.toString(16).padStart(2, '0');
    }
  } catch {
    /* fall through to Math.random */
  }
  if (out.length !== 32) {
    out = '';
    for (let i = 0; i < 32; i += 1) out += Math.floor(Math.random() * 16).toString(16);
  }
  // Shape it as a v4 UUID so intake's format check passes either way.
  return (
    `${out.slice(0, 8)}-${out.slice(8, 12)}-4${out.slice(13, 16)}-` +
    `a${out.slice(17, 20)}-${out.slice(20, 32)}`
  );
}

/**
 * Clamp a duration into `[0, max]` and round to whole ms.
 *
 * Returns undefined for anything that is not a finite number — a NaN from a
 * missing clock, an Infinity from a bad subtraction — because a fabricated 0
 * is indistinguishable from a genuinely instant stage in the aggregate.
 */
export function boundDurationMs(value: unknown, max: number): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  return Math.min(Math.max(0, Math.round(value)), max);
}

/**
 * The ONE filter between the SDK and the wire.
 *
 * Keeps allowlisted keys with a valid type and a valid enum/range; drops
 * everything else silently. Returns undefined when nothing survives, so the
 * item omits `attributes` entirely rather than sending `{}`.
 */
export function sanitizeDiagnosticAttributes(
  raw: SdkDiagnosticAttributes | undefined,
): SdkDiagnosticAttributes | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const out: SdkDiagnosticAttributes = {};
  if (ALLOWED_REASONS.includes(raw.reason as SdkDiagnosticReason)) out.reason = raw.reason;
  if (isAllowedStage(raw.stage)) out.stage = raw.stage;
  if (typeof raw.prefetched === 'boolean') out.prefetched = raw.prefetched;
  const durationMs = boundDurationMs(raw.durationMs, MAX_STARTUP_DURATION_MS);
  if (durationMs !== undefined) out.durationMs = durationMs;
  const sessionDurationMs = boundDurationMs(raw.sessionDurationMs, MAX_SESSION_DURATION_MS);
  if (sessionDurationMs !== undefined) out.sessionDurationMs = sessionDurationMs;
  return Object.keys(out).length > 0 ? out : undefined;
}

/** Everything the client needs from the SDK, all pulled at emit/flush time. */
export interface SdkDiagnosticsOptions {
  apiUrl: () => string;
  clientKey: () => string;
  agentMode: () => SdkDiagnosticAgentMode;
  /**
   * Consent posture at this instant, or null when the client must stay silent.
   * Read per enqueue AND per flush so a `consent('denied')` landing between the
   * two cannot leak a queued batch.
   */
  consent: () => SdkDiagnosticConsent | null;
  /** Correlation id to use when nothing has started yet. */
  fallbackSessionId?: () => string;
  /** Injectable transports (tests). */
  send?: (url: string, body: string, useBeacon: boolean) => void;
  logger?: Logger;
  /** Wall clock, injectable for tests. */
  now?: () => number;
  /** Monotonic clock, injectable for tests. */
  monotonicNow?: () => number;
  /** Startup watchdog window (tests). */
  botReadyTimeoutMs?: number;
  /** Disable the page-hide listener (tests, non-DOM hosts). */
  bindUnload?: boolean;
}

/** One in-flight start, from `connect_started` to its outcome. */
interface DiagnosticStart {
  correlationId: string;
  t0: number;
  stage: SdkDiagnosticStage;
  firstAudioSent: boolean;
  pairTimeout: ReturnType<typeof setTimeout> | null;
}

function monotonicNowDefault(): number {
  try {
    return typeof performance !== 'undefined' && typeof performance.now === 'function'
      ? performance.now()
      : Date.now();
  } catch {
    return Date.now();
  }
}

export class SdkDiagnosticsClient {
  private readonly options: SdkDiagnosticsOptions;
  private readonly logger: Logger;
  private readonly queue: EventQueue<SdkDiagnosticEventItem>;
  /** C5 kill switch + consent teardown. Armed only after validate-client-key. */
  private enabled = false;
  private sequence = 0;
  private start: DiagnosticStart | null = null;
  private unmountBound = false;
  private pageHideHandler: (() => void) | null = null;
  private visibilityHandler: (() => void) | null = null;

  constructor(options: SdkDiagnosticsOptions) {
    this.options = options;
    this.logger = options.logger ?? defaultLogger;
    this.queue = new EventQueue<SdkDiagnosticEventItem>({
      url: () => this.endpoint(),
      buildBody: (events) => ({
        clientKey: this.options.clientKey(),
        // Resolved at flush time: a withdrawal after enqueue must not ride out
        // on the next debounce tick.
        consent: this.options.consent(),
        events,
      }),
      send: options.send ?? sendEventBatch,
      logger: this.logger,
    });
    if (options.bindUnload !== false) this.bindUnloadListener();
  }

  private endpoint(): string {
    return `${this.options.apiUrl()}${SDK_DIAGNOSTICS_PATH}`;
  }

  private monotonicNow(): number {
    return (this.options.monotonicNow ?? monotonicNowDefault)();
  }

  private wallClock(): number {
    try {
      return (this.options.now ?? Date.now)();
    } catch {
      return Date.now();
    }
  }

  /**
   * Every public entry point funnels its unexpected failure here. Diagnostics
   * exist to explain a broken session, never to become one: a thrown clock, a
   * hostile injected transport, or a queue in a bad state must not propagate
   * into `onBotReady`, `onDisconnected`, or a connect `catch` block that is
   * already handling the real error.
   */
  private warn(error: unknown): void {
    try {
      this.logger.warn('[SdkDiagnostics] suppressed failure:', error);
    } catch {
      /* nothing left to do */
    }
  }

  /** True when an event may leave the browser right now. */
  private gateOpen(): boolean {
    if (!this.enabled) return false;
    try {
      return this.options.consent() !== null;
    } catch {
      return false;
    }
  }

  /**
   * C5 / consent switch. Off clears the queue UNSENT and unbinds delivery —
   * mirroring VisitorEventsClient, so turning collection off never ships the
   * events collected while it was on.
   */
  setCollectionEnabled(enabled: boolean): void {
    try {
      this.enabled = enabled;
      if (enabled) {
        // dispose() unbinds the unload listeners; an SDK re-initialised after a
        // teardown (a demo retry) must re-arm them or its page-hide end would
        // never be reported.
        if (this.options.bindUnload !== false) this.bindUnloadListener();
        return;
      }
      this.resetStart();
      this.queue.clear();
      this.queue.dispose();
    } catch (e) {
      // A failed teardown must not become the reason a revoke does not take
      // effect: close the gate first, always.
      this.enabled = false;
      this.warn(e);
    }
  }

  /** Reset per-start state (a start ending, or the gate closing). */
  private resetStart(): void {
    if (this.start?.pairTimeout) {
      try {
        clearTimeout(this.start.pairTimeout);
      } catch {
        /* already fired */
      }
    }
    this.start = null;
  }

  private correlationId(): string {
    if (this.start) return this.start.correlationId;
    let candidate: unknown;
    try {
      candidate = this.options.fallbackSessionId?.();
    } catch {
      candidate = undefined;
    }
    if (isCorrelationId(candidate)) return candidate;
    return `session_${newDiagnosticEventId()}`;
  }

  /**
   * The single enqueue seam. Wrapped end-to-end: a malformed attribute, a
   * missing clock, or a throwing transport costs the visitor nothing.
   */
  private emit(event: SdkDiagnosticEventName, attributes?: SdkDiagnosticAttributes): void {
    try {
      if (!this.gateOpen()) return;
      const sanitized = sanitizeDiagnosticAttributes(attributes);
      const item: SdkDiagnosticEventItem = {
        schemaVersion: DIAGNOSTIC_SCHEMA_VERSION,
        eventId: newDiagnosticEventId(),
        sequence: this.sequence,
        correlationId: this.correlationId(),
        event,
        agentMode: this.options.agentMode(),
        sdkVersion: reportedSdkVersion(),
        timestamp: this.wallClock(),
        ...(sanitized ? { attributes: sanitized } : {}),
      };
      if (this.sequence < MAX_SEQUENCE) this.sequence += 1;
      this.queue.enqueue(item);
    } catch (e) {
      try {
        this.logger.warn('[SdkDiagnostics] emit failed:', e);
      } catch {
        /* nothing left to do */
      }
    }
  }

  /**
   * Begin a start. Called from every session START seam with the writer id the
   * seam just minted, so a diagnostic thread and the conversation row the API
   * writes name the same session.
   *
   * A start that opens while the gate is CLOSED is not recorded at all. That is
   * the prospective half of the consent contract: a visitor on a `pending` site
   * runs a real session before granting, and when they grant mid-session the
   * clock must not suddenly produce a "durationMs" measured from before the
   * grant, nor a watchdog that fires about a startup nobody consented to
   * report. Nothing collected pre-grant is ever replayed — there is nothing to
   * replay, because no start was ever opened.
   */
  startSession(options: { correlationId: string; prefetched?: boolean }): void {
    try {
      this.resetStart();
      if (!this.gateOpen()) return;
      const correlationId = isCorrelationId(options.correlationId)
        ? options.correlationId
        : `session_${newDiagnosticEventId()}`;
      this.start = {
        correlationId,
        t0: this.monotonicNow(),
        stage: 'session_open',
        firstAudioSent: false,
        pairTimeout: null,
      };
      this.emit('connect_started', { prefetched: options.prefetched === true });
      this.armBotReadyTimeout();
    } catch (e) {
      this.warn(e);
    }
  }

  private armBotReadyTimeout(): void {
    const timeoutMs = this.options.botReadyTimeoutMs ?? BOT_READY_TIMEOUT_MS;
    const start = this.start;
    if (!start) return;
    try {
      start.pairTimeout = setTimeout(() => {
        try {
          if (this.start !== start) return;
          if (stageAtLeast(start.stage, 'bot_ready')) return;
          this.emit('bot_ready_timeout', {
            reason: 'bot_ready_timeout',
            stage: start.stage,
            durationMs: timeoutMs,
          });
          // One report per start: the watchdog is a fact, not a heartbeat.
          start.pairTimeout = null;
        } catch (e) {
          start.pairTimeout = null;
          this.warn(e);
        }
      }, timeoutMs);
    } catch {
      start.pairTimeout = null;
    }
  }

  /** Advance the milestone. Monotonic: a replayed earlier stage cannot rewind. */
  noteStage(stage: SdkDiagnosticStage): void {
    try {
      if (!this.start) return;
      if (!stageAtLeast(stage, this.start.stage)) return;
      this.start.stage = stage;
    } catch (e) {
      this.warn(e);
    }
  }

  /**
   * One fine startup milestone (website-agent path only — that is the one that
   * arms `voiceStartupTimeline`). The mark's own offset from the click rides
   * along, so intake gets the segment without subtracting clocks.
   */
  noteStartupStage(stage: SdkStartupStage, msFromClick: number): void {
    try {
      if (!this.start) return;
      this.emit('startup_stage', { stage, durationMs: msFromClick });
    } catch (e) {
      this.warn(e);
    }
  }

  /** Milliseconds since this start began, bounded to the startup window. */
  private elapsedMs(): number | undefined {
    if (!this.start) return undefined;
    return this.elapsedSince(this.start.t0, MAX_STARTUP_DURATION_MS);
  }

  /**
   * Bounded elapsed time from `t0`. A clock that throws (an injected test
   * double, a UA with a poisoned `performance`) costs the DURATION, never the
   * event: the failure is still worth reporting without it.
   */
  private elapsedSince(t0: number, max: number): number | undefined {
    try {
      return boundDurationMs(this.monotonicNow() - t0, max);
    } catch {
      return undefined;
    }
  }

  /**
   * The start/connect path threw. `reason` defaults to the category implied by
   * how far startup got, which is the question triage actually asks; call sites
   * that KNOW why (mic denial, an aborted spawn) pass it explicitly.
   */
  noteConnectFailed(reason?: SdkDiagnosticReason): void {
    try {
      if (!this.start) return;
      const start = this.start;
      const stage = start.stage;
      const derived: SdkDiagnosticReason =
        stage === 'idle'
          ? 'unknown'
          : stage === 'session_open'
            ? 'start_session_failed'
            : 'transport_error';
      this.emit('connect_failed', {
        reason: reason ?? derived,
        stage,
        durationMs: this.elapsedMs(),
      });
      // A failed start is not also a session that ended — connect_failed
      // carries its own duration, and emitting both would double-count it.
      this.resetStart();
    } catch (e) {
      this.warn(e);
    }
  }

  /**
   * The visitor's microphone could not be used. Callers pass a category they
   * can actually tell apart; an unclassified failure is not reported as a
   * denial, because "the visitor said no" and "the OS would not release the
   * device" are different investigations.
   */
  noteMicDenied(reason: SdkDiagnosticMicReason): void {
    try {
      this.emit('mic_denied', { reason, stage: this.start?.stage ?? 'idle' });
    } catch (e) {
      this.warn(e);
    }
  }

  /** The transport dropped. `client_requested` when the SDK asked it to. */
  noteTransportDisconnected(clientRequested: boolean): void {
    try {
      this.emit('transport_disconnected', {
        reason: clientRequested ? 'client_requested' : 'transport_closed',
        stage: this.start?.stage ?? 'idle',
        durationMs: this.elapsedMs(),
      });
    } catch (e) {
      this.warn(e);
    }
  }

  /** First audible bot turn of this start. Once per start, by construction. */
  noteFirstAudio(): void {
    try {
      const start = this.start;
      if (!start || start.firstAudioSent) return;
      start.firstAudioSent = true;
      start.stage = 'first_audio';
      this.emit('first_audio', { stage: 'first_audio', durationMs: this.elapsedMs() });
    } catch (e) {
      this.warn(e);
    }
  }

  /**
   * End the active start. Once per start — `resetStart()` in the same call is
   * what makes a double teardown (disconnect + onDisconnected) a no-op.
   */
  endSession(reason: SdkDiagnosticReason): void {
    try {
      const start = this.start;
      if (!start) return;
      const sessionDurationMs = this.elapsedSince(start.t0, MAX_SESSION_DURATION_MS);
      this.emit('session_ended', { reason, sessionDurationMs });
      this.resetStart();
    } catch (e) {
      this.warn(e);
    }
  }

  /**
   * The page is going away: report the end and flush it, because this is the
   * last moment the browser will run. Bound independently of EventQueue's own
   * handler so listener order cannot decide whether the event ships.
   */
  private handlePageHide(): void {
    try {
      this.endSession('page_hidden');
      this.queue.flush(true);
    } catch {
      /* best-effort by design */
    }
  }

  /**
   * The tab went to the background. FLUSH ONLY — never end the session.
   *
   * A hidden tab is not a finished call: the widget keeps rendering, the
   * transport stays connected, the bot keeps talking, and the visitor comes
   * back. Treating `visibilitychange` as an ending closed the diagnostics
   * thread, which then swallowed the real `first_audio` and the real
   * `session_ended` for that start AND published a fabricated "session ended"
   * for a call that was still up. The queue's final flush still runs, so a
   * batch already collected is not lost; the thread simply stays open.
   */
  private handleVisibilityHidden(): void {
    try {
      this.queue.flush(true);
    } catch {
      /* best-effort by design */
    }
  }

  private bindUnloadListener(): void {
    if (this.unmountBound || typeof window === 'undefined') return;
    this.unmountBound = true;
    this.pageHideHandler = () => this.handlePageHide();
    this.visibilityHandler = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        this.handleVisibilityHidden();
      }
    };
    window.addEventListener('pagehide', this.pageHideHandler);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.visibilityHandler);
    }
  }

  /**
   * Final flush + full unbind. After this the client is INERT: the gate is
   * closed too, so a stray callback from a half-torn-down session cannot
   * re-enqueue through a queue that is no longer listening for unload.
   */
  dispose(): void {
    try {
      this.endSession('client_requested');
      this.queue.dispose();
      if (this.pageHideHandler && typeof window !== 'undefined') {
        window.removeEventListener('pagehide', this.pageHideHandler);
      }
      if (this.visibilityHandler && typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', this.visibilityHandler);
      }
    } catch (e) {
      this.warn(e);
    } finally {
      this.pageHideHandler = null;
      this.visibilityHandler = null;
      this.unmountBound = false;
      this.resetStart();
      this.enabled = false;
    }
  }

  /** Queue depth — tests and debugging only. */
  get pendingCount(): number {
    return this.queue.size;
  }
}
